import type { TranslationKey } from "@/src/i18n";
import type { MarkerObjectCategory, MarkerObjectType, StampAssetId } from "@/src/types/map";

/** Declarative draw hints, same approach as the wall and floor catalogues. */
export interface MarkerObjectStyle {
  /** Centre glyph, dungeon-key convention. */
  glyph?: string;
  /** Solid round or square column. */
  column?: "round" | "square";
  /** Plinth-and-figure silhouette. */
  figure?: boolean;
  /** Rectangular block: altar, dais, throne all share it with different detail. */
  block?: "altar" | "dais" | "throne";
  /** Flame bowl. */
  flame?: boolean;
  /** Lidded box with a material band. */
  chest?: "wood" | "iron" | "gold";
}

export interface MarkerObjectDefinition {
  id: MarkerObjectType;
  category: MarkerObjectCategory;
  placement: "grid-snap";
  defaultWidthCells: number;
  defaultHeightCells: number;
  style: MarkerObjectStyle;
  labelKey: TranslationKey;
  dmOnly: boolean;
}

export const MARKER_OBJECT_CATALOG: MarkerObjectDefinition[] = [
  { id: "trap", category: "markers", placement: "grid-snap", defaultWidthCells: 0.8, defaultHeightCells: 0.8,
    style: { glyph: "T" }, labelKey: "marker.trap", dmOnly: true },

  { id: "pillar_round", category: "architecture", placement: "grid-snap", defaultWidthCells: 0.7, defaultHeightCells: 0.7,
    style: { column: "round" }, labelKey: "marker.pillar_round", dmOnly: false },
  { id: "pillar_square", category: "architecture", placement: "grid-snap", defaultWidthCells: 0.7, defaultHeightCells: 0.7,
    style: { column: "square" }, labelKey: "marker.pillar_square", dmOnly: false },
  { id: "dais", category: "architecture", placement: "grid-snap", defaultWidthCells: 2, defaultHeightCells: 2,
    style: { block: "dais" }, labelKey: "marker.dais", dmOnly: false },

  { id: "statue", category: "furnishings", placement: "grid-snap", defaultWidthCells: 0.8, defaultHeightCells: 1.2,
    style: { figure: true }, labelKey: "marker.statue", dmOnly: false },
  { id: "altar", category: "furnishings", placement: "grid-snap", defaultWidthCells: 1.6, defaultHeightCells: 0.9,
    style: { block: "altar" }, labelKey: "marker.altar", dmOnly: false },
  { id: "throne", category: "furnishings", placement: "grid-snap", defaultWidthCells: 1, defaultHeightCells: 1.2,
    style: { block: "throne" }, labelKey: "marker.throne", dmOnly: false },
  { id: "brazier", category: "furnishings", placement: "grid-snap", defaultWidthCells: 0.7, defaultHeightCells: 0.7,
    style: { flame: true }, labelKey: "marker.brazier", dmOnly: false },
  { id: "chest_wood", category: "furnishings", placement: "grid-snap", defaultWidthCells: 1, defaultHeightCells: 0.6,
    style: { chest: "wood" }, labelKey: "marker.chest_wood", dmOnly: false },
  { id: "chest_iron", category: "furnishings", placement: "grid-snap", defaultWidthCells: 1, defaultHeightCells: 0.6,
    style: { chest: "iron" }, labelKey: "marker.chest_iron", dmOnly: false },
  { id: "chest_gold", category: "furnishings", placement: "grid-snap", defaultWidthCells: 1, defaultHeightCells: 0.6,
    style: { chest: "gold" }, labelKey: "marker.chest_gold", dmOnly: false },
];

export const DEFAULT_MARKER_TYPE: MarkerObjectType = "pillar_round";
export const MARKER_OBJECT_ASSET = "marker" as const;

const BY_ID = new Map(MARKER_OBJECT_CATALOG.map((entry) => [entry.id, entry]));

export const getMarkerObject = (type: MarkerObjectType | undefined): MarkerObjectDefinition =>
  (type ? BY_ID.get(type) : undefined) ?? MARKER_OBJECT_CATALOG[1]!;

export const isMarkerObjectType = (value: unknown): value is MarkerObjectType =>
  typeof value === "string" && BY_ID.has(value as MarkerObjectType);

export const MARKER_OBJECT_CATEGORIES: readonly MarkerObjectCategory[] =
  ["markers", "architecture", "furnishings"];

/**
 * Stamps that this family replaces. Objects using them are rewritten to
 * assetId "marker" plus the mapped type, so there is exactly one way to place
 * each of these. Used by both the v3 -> v4 migrator and the JSON import path,
 * so a legacy file behaves the same however it arrives.
 */
export const LEGACY_STAMP_TO_MARKER: Partial<Record<StampAssetId, MarkerObjectType>> = {
  pillar: "pillar_round",
  statue: "statue",
  brazier: "brazier",
  chest: "chest_wood",
  chest_wood: "chest_wood",
  chest_iron: "chest_iron",
  chest_gold: "chest_gold",
};

/** Rewrites one legacy stamp object in place-safe fashion; returns it unchanged otherwise. */
export const normaliseLegacyMarker = <T extends { assetId?: unknown; markerType?: unknown }>(object: T): T => {
  const assetId = object?.assetId as StampAssetId | undefined;
  if (!assetId || object.markerType !== undefined) return object;
  const markerType = LEGACY_STAMP_TO_MARKER[assetId];
  if (!markerType) return object;
  return { ...object, assetId: MARKER_OBJECT_ASSET, markerType };
};
